// SAB-493 Phase 3: shared helpers that feed the 4c GeoBlockScreen from the
// PROFILE-ELIGIBILITY layer (check-eligibility get_user_status), plus the
// live-gate for the "Play free-to-play" CTA.
//
// This is the DECLARED-state layer (the state the user registered in), distinct
// from the Locance layer's PHYSICALLY-DETECTED state (useLocanceGeoCheck). Both
// funnel into the same screen but carry stateSource so copy stays correct.
//
// GUARDRAIL: display-only. Nothing here gates money — the deposit/withdraw/join
// server paths (KYC, RG, Locance verdict) remain the authoritative gates.

import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { GeoBlockReason } from '@/hooks/useLocanceGeoCheck';

/** Shape of check-eligibility `get_user_status` we care about. */
export interface EligibilityStatus {
  verified?: boolean;
  eligible?: boolean;
  state_name?: string | null;
  state_code?: string | null;
}

/**
 * Map a check-eligibility `get_user_status` result to a GeoBlockReason for the
 * profile layer, or null if the profile layer doesn't block.
 *   - !verified                → profile_incomplete (user-fixable: verify state/age)
 *   - verified && !eligible     → out_of_region using the DECLARED state
 * Note: this is the PROFILE gate. The Locance physical-location gate is separate
 * (useLocanceGeoCheck.geoBlock) and runs after this passes.
 */
export function eligibilityToGeoBlock(res: EligibilityStatus | null | undefined): GeoBlockReason | null {
  if (!res) return null;
  if (!res.verified) return { kind: 'profile_incomplete' };
  if (res.verified && res.eligible === false) {
    return { kind: 'out_of_region', detectedState: res.state_name ?? null, stateSource: 'declared' };
  }
  return null;
}

/**
 * Live-gate for the "Play free-to-play instead" CTA on the out_of_region screen.
 * True only when ≥1 joinable free league exists — mirrors the browse route's
 * filter: visibility='public', entry_fee=0, status in (setup|pre_draft), and an
 * open seat (filled teams < max_teams). Fetches once on mount; defaults false so
 * we never show the CTA before we know it lands somewhere real.
 */
export function useFreePlayAvailable(): boolean {
  const [available, setAvailable] = useState(false);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { data, error } = await supabase
          // SAB security H3: safe public-browse view (no direct nfl_leagues read).
          .from('nfl_public_leagues')
          .select('id, max_teams, filled_teams')
          .eq('entry_fee', 0)
          .in('status', ['setup', 'pre_draft'])
          .limit(25);
        if (cancelled || error || !data) return;
        const anyOpen = data.some((l) => {
          const filled = Number((l as { filled_teams?: number }).filled_teams ?? 0);
          return filled < (l as { max_teams: number }).max_teams;
        });
        setAvailable(anyOpen);
      } catch {
        // best-effort — a failed check just hides the CTA (safe default).
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  return available;
}
